import { AirwallexErrorCode, AirwallexRecipientValidationResult } from '@40seas/entities/airwallex';
import {
    BankAccount,
    BankAccountDetails,
    BankAccountLocation,
    BankAccountScheme,
    CreateBankAccountDto,
} from '@40seas/entities/bank-account';
import { Country } from '@40seas/entities/common';
import { FinancialConnectionProviderType } from '@40seas/entities/financial-connections';
import { Address, Currency } from '@40seas/models';
import { TENANT_PREFIXES_WITH_COMBINED_VA } from '@40seas/shared/payment';
import auStates from 'libs/shared/address/src/lib/statesProvider/auStates';
import caStates from 'libs/shared/address/src/lib/statesProvider/caStates';
import { abbr } from 'us-state-converter';

export function getCurrencyDisplay(bankAccount: BankAccount): string {
    const { currency } = bankAccount?.bankAccountDetails || {};

    const currencyMappings = new Map<Currency, string>([
        [Currency.USD, `🇺🇲 ${Currency.USD}`],
        [Currency.EUR, `🇪🇺 ${Currency.EUR}`],
        [Currency.GBP, `🇬🇧 ${Currency.GBP}`],
        [Currency.AUD, `🇦🇺 ${Currency.AUD}`],
        [Currency.CAD, `🇨🇦 ${Currency.CAD}`],
        [Currency.CNH, `🇨🇳 ${Currency.CNH}`],
        [Currency.CNY, `🇨🇳 ${Currency.CNY}`],
        [Currency.JPY, `🇯🇵 ${Currency.JPY}`],
        [Currency.HKD, `🇭🇰 ${Currency.HKD}`],
    ]);

    return currencyMappings.get(currency) || '';
}

export function getCurrenciesChoices() {
    return [
        { id: 'USD', name: `🇺🇲 ${Currency.USD}` },
        { id: 'EUR', name: `🇪🇺 ${Currency.EUR}` },
        { id: 'GBP', name: `🇬🇧 ${Currency.GBP}` },
        { id: 'AUD', name: `🇦🇺 ${Currency.AUD}` },
        { id: 'CAD', name: `🇨🇦 ${Currency.CAD}` },
        { id: 'CNH', name: `🇨🇳 ${Currency.CNH}` },
        { id: 'CNY', name: `🇨🇳 ${Currency.CNY}` },
        { id: 'JPY', name: `🇯🇵 ${Currency.JPY}` },
        { id: 'HKD', name: `🇭🇰 ${Currency.HKD}` },
    ];
}

export function getBankAccountDisplay(bankAccount: BankAccount): string {
    const { bankAccountName, bankAccountPayload } = bankAccount?.bankAccountDetails || {};

    let bankAccountDisplay = '';

    if (bankAccountName) {
        bankAccountDisplay = bankAccountName;
    }

    if (bankAccountPayload && (bankAccountPayload['accountNumber'] || bankAccountPayload['iban'])) {
        bankAccountDisplay += partialBankAccount(bankAccountPayload['accountNumber'] || bankAccountPayload['iban']);
    }

    return bankAccountDisplay;
}

const partialBankAccount = (bankAccount: string): string => {
    return `....${bankAccount.substring(bankAccount.length - 4)}`;
};

export const accountLocationDictionary: { [key in Currency]?: { id: BankAccountLocation; name: string }[] } = {
    [Currency.USD]: [
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.USD.domestic' },
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.USD.international' },
    ],
    [Currency.EUR]: [
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.EUR.domestic' },
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.EUR.international' },
    ],
    [Currency.GBP]: [
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.GBP.domestic' },
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.GBP.international' },
    ],
    [Currency.AUD]: [],
    [Currency.CAD]: [],
    [Currency.CNH]: [{ id: BankAccountLocation.International, name: 'accountLocationDictionary.CNH.international' }],
    [Currency.CNY]: [
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.CNY.international' },
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.CNY.domestic' },
    ],
    [Currency.JPY]: [
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.JPY.domestic' },
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.JPY.international' },
    ],
    [Currency.HKD]: [
        { id: BankAccountLocation.Domestic, name: 'accountLocationDictionary.HKD.domestic' },
        { id: BankAccountLocation.International, name: 'accountLocationDictionary.HKD.international' },
    ],
};

export const bankAccountSchemeDictionary = {
    [`${Currency.USD}_${BankAccountLocation.Domestic}`]: BankAccountScheme.ACH,
    [`${Currency.USD}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
    [`${Currency.EUR}_${BankAccountLocation.Domestic}`]: BankAccountScheme.IBAN,
    [`${Currency.EUR}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
    [`${Currency.GBP}_${BankAccountLocation.Domestic}`]: BankAccountScheme.BACS,
    [`${Currency.GBP}_${BankAccountLocation.International}`]: BankAccountScheme.IBAN,
    [`${Currency.AUD}_${BankAccountLocation.Domestic}`]: BankAccountScheme.BECS,
    [`${Currency.AUD}_${BankAccountLocation.International}`]: BankAccountScheme.BECS,
    [`${Currency.CAD}_${BankAccountLocation.International}`]: BankAccountScheme.ACSS,
    [`${Currency.CAD}_${BankAccountLocation.Domestic}`]: BankAccountScheme.ACSS,
    [`${Currency.CNH}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
    [`${Currency.CNY}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
    [`${Currency.CNY}_${BankAccountLocation.Domestic}`]: BankAccountScheme.CNAPS,
    [`${Currency.JPY}_${BankAccountLocation.Domestic}`]: BankAccountScheme.FPS,
    [`${Currency.JPY}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
    [`${Currency.HKD}_${BankAccountLocation.Domestic}`]: BankAccountScheme.FPS,
    [`${Currency.HKD}_${BankAccountLocation.International}`]: BankAccountScheme.SWIFT,
};

export const accountFieldsDictionary = {
    [`${Currency.USD}_${BankAccountLocation.Domestic}`]: [
        { source: 'routingNumber', label: 'accountFieldsDictionary.USDDomestic.routingNumber' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.USDDomestic.accountNumber' },
    ],
    [`${Currency.USD}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.USDInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.USDInternational.accountNumber' },
    ],
    [`${Currency.EUR}_${BankAccountLocation.Domestic}`]: [
        {
            source: 'iban',
            label: 'accountFieldsDictionary.EURDomestic.iban',
        },
    ],
    [`${Currency.EUR}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.EURInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.EURInternational.accountNumber' },
    ],
    [`${Currency.GBP}_${BankAccountLocation.Domestic}`]: [
        { source: 'sortCode', label: 'accountFieldsDictionary.GBPDomestic.sortCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.GBPDomestic.accountNumber' },
    ],
    [`${Currency.GBP}_${BankAccountLocation.International}`]: [
        {
            source: 'iban',
            label: 'accountFieldsDictionary.GBInternational.iban',
        },
    ],
    [`${Currency.AUD}_${BankAccountLocation.Domestic}`]: [
        { source: 'bsbCode', label: 'accountFieldsDictionary.AUDDomestic.bsb' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.AUDDomestic.accountNumber' },
    ],
    [`${Currency.AUD}_${BankAccountLocation.International}`]: [
        { source: 'bsbCode', label: 'accountFieldsDictionary.AUDInternational.bsb' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.AUDInternational.accountNumber' },
    ],
    [`${Currency.CAD}_${BankAccountLocation.International}`]: [
        { source: 'institutionNumber', label: 'accountFieldsDictionary.CADDomestic.institutionNumber' },
        { source: 'transitNumber', label: 'accountFieldsDictionary.CADDomestic.transitNumber' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.CADDomestic.accountNumber' },
    ],
    [`${Currency.CAD}_${BankAccountLocation.Domestic}`]: [
        { source: 'institutionNumber', label: 'accountFieldsDictionary.CADDomestic.institutionNumber' },
        { source: 'transitNumber', label: 'accountFieldsDictionary.CADDomestic.transitNumber' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.CADDomestic.accountNumber' },
    ],
    [`${Currency.CNH}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.CNHInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.CNHInternational.accountNumber' },
    ],
    [`${Currency.CNY}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.CNYInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.CNYInternational.accountNumber' },
    ],
    [`${Currency.CNY}_${BankAccountLocation.Domestic}`]: [
        { source: 'routingNumber', label: 'accountFieldsDictionary.CNYDomestic.routingNumber' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.CNYDomestic.accountNumber' },
    ],
    [`${Currency.JPY}_${BankAccountLocation.Domestic}`]: [
        { source: 'accountNumber', label: 'accountFieldsDictionary.JPYDomestic.accountNumber' },
        { source: 'bankCode', label: 'accountFieldsDictionary.JPYDomestic.bankCode' },
        { source: 'branchCode', label: 'accountFieldsDictionary.JPYDomestic.branchCode' },
    ],
    [`${Currency.JPY}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.JPYInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.JPYInternational.accountNumber' },
    ],
    [`${Currency.HKD}_${BankAccountLocation.International}`]: [
        { source: 'swiftCode', label: 'accountFieldsDictionary.HKDInternational.swiftCode' },
        { source: 'accountNumber', label: 'accountFieldsDictionary.HKDInternational.accountNumber' },
    ],
    [`${Currency.HKD}_${BankAccountLocation.Domestic}`]: [
        { source: 'accountNumber', label: 'accountFieldsDictionary.HKDDomestic.accountNumber' },
        { source: 'bankCode', label: 'accountFieldsDictionary.HKDDomestic.bankCode' },
        { source: 'branchCode', label: 'accountFieldsDictionary.HKDDomestic.branchCode' },
    ],
};

export function isBankAccount(type: FinancialConnectionProviderType) {
    return ![
        FinancialConnectionProviderType.Documents,
        FinancialConnectionProviderType.PersonalGuarantee,
        FinancialConnectionProviderType.Codat,
        FinancialConnectionProviderType.Rutter,
        FinancialConnectionProviderType.OrdersHistory,
    ].includes(type);
}

function isTenantWithOwnBank(tenantId: string) {
    return TENANT_PREFIXES_WITH_COMBINED_VA.some((prefix) => tenantId.startsWith(prefix));
}

export function getBankAccountName(
    bankAccountDetails: BankAccountDetails | undefined,
    tenantId: string | undefined,
    tenantBusinessName: string | undefined,
) {
    if (tenantId && isTenantWithOwnBank(tenantId)) {
        return bankAccountDetails?.bankAccountName;
    }

    return tenantBusinessName;
}

export function getBankName(bankAccountDetails: BankAccountDetails | undefined) {
    if (bankAccountDetails?.bankName) {
        return bankAccountDetails?.bankName;
    }

    // TODO : This can be removed, we should always have bankName
    if (bankAccountDetails?.currency && [(Currency.CAD, Currency.USD)].includes(bankAccountDetails?.currency)) {
        return 'Wells Fargo'; // USD and CAd are Stripe, and Stripe is always Wells Fargo
    }

    return 'Wise';
}

export async function createBankAccount(
    createDto: CreateBankAccountDto,
    validate: (createDto: CreateBankAccountDto) => Promise<string | AirwallexRecipientValidationResult>,
    create: (createBankAccountDto: CreateBankAccountDto) => Promise<any>,
    localeField?: (field: string) => string,
): Promise<BankAccount | { errors: Record<string, any> }> {
    const validationResult = await validate(createDto);

    const fieldsMapping = {
        'beneficiary.address.country_code': 'country',
        'beneficiary.address.city': 'city',
        'beneficiary.address.street_address': 'addressLine1',
        'beneficiary.address.postcode': 'zipCode',
        'beneficiary.address.state': 'state',

        'beneficiary.bank_details.account_currency': 'currency',
        'beneficiary.bank_details.account_number': 'accountNumber',
        'beneficiary.bank_details.account_routing_value1': createDto?.bankAccountDetails?.currency === 'AUD' ? 'bsb' : 'routingNumber',
        'beneficiary.bank_details.bank_country_code': 'country',
        'beneficiary.bank_details.account_name': 'bankAccountName',
        'beneficiary.bank_details.bank_name': 'bankName',
        'beneficiary.bank_details.iban': 'iban',
        'beneficiary.bank_details.account_routing_value2': 'sortCode',
        'beneficiary.bank_details.swift_code': 'SWIFT code',
    };

    const errorsBody = (
        Array.isArray((validationResult as AirwallexRecipientValidationResult)?.errors)
            ? (validationResult as AirwallexRecipientValidationResult).errors
            : []
    ).reduce(
        (acc, { source, code, params }) => {
            const src = localeField?.[fieldsMapping[source]]?.() || source;
            acc.errors[src] = `${AirwallexErrorCode.getDescription(code)}${params?.length ? ' params:' + JSON.stringify(params) : ''}`;

            return acc;
        },
        { errors: {} } as { errors: Record<string, any> },
    );
    const hasErrors = typeof validationResult !== 'string';

    if (hasErrors) {
        const err = validationResult as AirwallexRecipientValidationResult;

        if (!Object.keys(errorsBody.errors).length) {
            errorsBody.errors['error'] = err.code;
        }
        return { errors: errorsBody.errors };
    }

    const bankAccount = await create(createDto);

    return bankAccount;
}

export function normalizeBankAddress(bankAddress: Address): Address {
    const { country, zipCode, state, city } = bankAddress;
    const [computedCity, computedState] = city?.split(', ') || [];
    const abbreviationRequired = computedState?.length > 2;

    if (!zipCode && country && [Country.HONG_KONG, Country.UNITED_ARAB_EMIRATES].includes(country)) {
        return { ...bankAddress, zipCode: '00000' };
    }

    if (!state && computedState && country && country === Country.UNITED_STATES) {
        const stateAbbreviation = abbreviationRequired ? abbr(computedState) : computedState;
        return { ...bankAddress, city: computedCity, state: stateAbbreviation };
    }

    if (!state && computedState && country && [Country.CANADA, Country.AUSTRALIA].includes(country)) {
        const states = country === Country.CANADA ? caStates : auStates;
        const stateAbbreviation = abbreviationRequired ? states.find((province) => province.name === computedState)?.code : computedState;
        return { ...bankAddress, city: computedCity, state: stateAbbreviation };
    }

    return bankAddress;
}
